import { onCLS, onFCP, onLCP, onTTFB, onINP } from 'web-vitals';
import type { MetricMap, MetricName } from '@/types';
import { METRIC_META } from '@/data/data';
import { getPerformanceRating, formatMetricValue } from './performanceUtils';

class WebVitalsMonitor {
  private metrics: Partial<Record<MetricName, number>> = {};
  private isMonitoring: boolean = false;

  startMonitoring() {
    if (this.isMonitoring) {
      console.warn('Web Vitals 监控已经在运行中');
      return;
    }

    this.isMonitoring = true;
    console.log('🚀 Web Vitals 性能监控已启动...\n');

    this.monitorMetric('CLS', onCLS);
    this.monitorMetric('FCP', onFCP);
    this.monitorMetric('LCP', onLCP);
    this.monitorMetric('TTFB', onTTFB);
    this.monitorMetric('INP', onINP);
  }

  private monitorMetric(
    metricName: MetricName,
    metricFn: (callback: (metric: { value: number }) => void) => void
  ) {
    metricFn((metric) => {
      this.metrics[metricName] = metric.value;
      this.reportMetric(metricName, metric.value);
    });
  }

  private reportMetric(metricName: MetricName, value: number) {
    const metricMeta = (METRIC_META as MetricMap)[metricName];
    if (!metricMeta) return;

    const rating = getPerformanceRating(metricName, value);
    const formattedValue = formatMetricValue(metricName, value);

    let color = '';
    let icon = '';

    switch (rating) {
      case '非常棒':
        color = '\x1b[32m';
        icon = '✅';
        break;
      case '有待改进':
        color = '\x1b[33m';
        icon = '⚠️';
        break;
      case '较差':
        color = '\x1b[31m';
        icon = '❌';
        break;
      default:
        color = '\x1b[37m';
        icon = 'ℹ️';
    }

    const resetColor = '\x1b[0m';

    console.log(
      `${icon} ${color}${metricName}: ${formattedValue} - ${rating}${resetColor}`
    );
    console.log(`   描述: ${metricMeta.cn}`);
    console.log(
      `   标准: good <= ${metricMeta.good}${metricMeta.unit}, ni <= ${metricMeta.ni}${metricMeta.unit}\n`
    );
  }

  getMetrics() {
    return { ...this.metrics };
  }

  getPerformanceSummary() {
    const summary: Record<string, unknown> = {};

    Object.keys(this.metrics).forEach((metricName) => {
      const value = this.metrics[metricName as MetricName]!;
      summary[metricName] = {
        value,
        formattedValue: formatMetricValue(metricName as MetricName, value),
        rating: getPerformanceRating(metricName as MetricName, value),
        description:
          (METRIC_META as MetricMap)[metricName as MetricName]?.cn || '',
      };
    });

    return summary;
  }

  stopMonitoring() {
    this.isMonitoring = false;
    console.log('🛑 Web Vitals 性能监控已停止');
  }
}

const webVitalsMonitor = new WebVitalsMonitor();

export const reportWebVitals = () => {
  webVitalsMonitor.startMonitoring();
};

export const getPerformanceReport = () => {
  return webVitalsMonitor.getPerformanceSummary();
};

export const stopWebVitalsMonitoring = () => {
  webVitalsMonitor.stopMonitoring();
};

export { WebVitalsMonitor };

export default webVitalsMonitor;